import { prefetchBriefLlm, useBriefLlm } from "./useBriefLlm.js";
import { bottomLinePrompt, cleanBottomLine } from "../lib/report/brief.js";

/**
 * The brief's bottom-line "Why" (the assessment's main rationale) and "The risk" (the biggest
 * open questions), written once per report by the small LLM and cached in this browser. Null
 * until they arrive, when writing fails, or while `enabled` is false — the bottom line then
 * falls back to the strongest reason and the first open question.
 *
 * @param {{ enabled: boolean, question: string, narrative: string, uncertainties: string[] }} deps
 * @returns {{ lines: {why: string, risk: string, view?: string}|null, pending: boolean }}
 *   `view` is Sayer's View rewritten not to repeat why and risk, when one could be written
 */
export function useBriefBottomLine({ enabled, question, narrative, uncertainties }) {
  const spec = bottomLineSpec({ question, narrative, uncertainties });
  const { value, pending } = useBriefLlm({ ...spec, enabled: !!(enabled && spec.ready) });
  return { lines: value, pending };
}

/** The bottom line's request; `ready` once both its sources exist. */
function bottomLineSpec({ question, narrative, uncertainties }) {
  const list = Array.isArray(uncertainties) ? uncertainties : [];
  return {
    // The assessment is the source; with no open questions listed, "risk" comes from its
    // own caveats (bottomLinePrompt says so).
    ready: !!narrative,
    name: "briefBottomLine",
    version: 4,
    inputs: [question, narrative, list],
    prompt: () => bottomLinePrompt({ question, narrative, uncertainties: list }),
    // The view's figures are checked against everything the prompt was given.
    clean: (text) => cleanBottomLine(text, [question, narrative, ...list].join("\n")),
  };
}

/** Starts writing the bottom line before the brief opens (see prefetchBriefLlm). */
export function prefetchBriefBottomLine(deps) {
  const spec = bottomLineSpec(deps);
  if (spec.ready) prefetchBriefLlm(spec);
}
